import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import {
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  Loader2,
  Star,
  Eye,
  EyeOff,
  LogOut,
  FileText,
  ArrowLeft,
  Users
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { BecomeLogo } from "../components/BecomeLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  "Career Transitions",
  "Emotional Readiness",
  "Platform Updates",
  "Research & Insights"
];

const emptyPost = {
  title: "",
  slug: "",
  excerpt: "",
  content: "",
  category: "Career Transitions",
  image_url: "",
  is_featured: false,
  is_published: true
};

// Helper to get auth header
const getAuthHeader = () => {
  const token = localStorage.getItem("adminToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function AdminBlogPage() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState(emptyPost);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin/login");
      return;
    }

    try {
      await axios.get(`${API}/admin/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsAuthenticated(true);
      fetchPosts();
    } catch (error) {
      console.error("Auth check failed:", error);
      localStorage.removeItem("adminToken");
      localStorage.removeItem("adminTokenExpires");
      navigate("/admin/login");
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/admin/logout`, {}, {
        headers: getAuthHeader()
      });
    } catch (error) {
      console.error("Logout error:", error);
    }
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminTokenExpires");
    toast.success("Logged out successfully");
    navigate("/admin/login");
  };

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog?published_only=false`);
      setPosts(response.data);
    } catch (error) {
      console.error("Error fetching posts:", error);
      toast.error("Failed to fetch posts");
    } finally {
      setIsLoading(false);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .trim();
  };

  const handleTitleChange = (value) => {
    setFormData({
      ...formData,
      title: value,
      slug: editingPost ? formData.slug : generateSlug(value)
    });
  };

  const handleOpenCreate = () => {
    setEditingPost(null);
    setFormData(emptyPost);
    setIsDialogOpen(true);
  };

  const handleOpenEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt,
      content: post.content,
      category: post.category,
      image_url: post.image_url || "",
      is_featured: post.is_featured,
      is_published: post.is_published
    });
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.title || !formData.excerpt || !formData.content) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSaving(true);
    try {
      if (editingPost) {
        await axios.put(`${API}/blog/${editingPost.id}`, formData, {
          headers: getAuthHeader()
        });
        toast.success("Post updated successfully");
      } else {
        await axios.post(`${API}/blog`, formData, {
          headers: getAuthHeader()
        });
        toast.success("Post created successfully");
      }
      setIsDialogOpen(false);
      fetchPosts();
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Session expired. Please login again.");
        navigate("/admin/login");
        return;
      }
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error("Failed to save post");
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm("Are you sure you want to delete this post?")) return;

    try {
      await axios.delete(`${API}/blog/${postId}`, {
        headers: getAuthHeader()
      });
      toast.success("Post deleted successfully");
      fetchPosts();
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Session expired. Please login again.");
        navigate("/admin/login");
        return;
      }
      toast.error("Failed to delete post");
    }
  };

  const toggleFeatured = async (post) => {
    try {
      await axios.put(`${API}/blog/${post.id}`, {
        is_featured: !post.is_featured
      }, {
        headers: getAuthHeader()
      });
      fetchPosts();
      toast.success(post.is_featured ? "Removed from featured" : "Set as featured");
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Session expired. Please login again.");
        navigate("/admin/login");
        return;
      }
      toast.error("Failed to update post");
    }
  };

  const togglePublished = async (post) => {
    try {
      await axios.put(`${API}/blog/${post.id}`, {
        is_published: !post.is_published
      }, {
        headers: getAuthHeader()
      });
      fetchPosts();
      toast.success(post.is_published ? "Post unpublished" : "Post published");
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Session expired. Please login again.");
        navigate("/admin/login");
        return;
      }
      toast.error("Failed to update post");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Show nothing while checking auth
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-warm-bone flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-warm-bone">
      {/* Admin Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-deep-violet border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/admin/dashboard" className="flex items-center gap-2 text-white/60 hover:text-white transition-colors">
                <ArrowLeft className="w-4 h-4" />
                Dashboard
              </Link>
              <span className="text-white/60">|</span>
              <span className="text-white font-medium flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Blog Posts
              </span>
            </div>
            <div className="flex items-center gap-4">
              <Link 
                to="/admin/dashboard"
                className="text-white/60 hover:text-white text-sm flex items-center gap-2 transition-colors"
              >
                <Users className="w-4 h-4" />
                Waitlist
              </Link>
              <a 
                href="/blog" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-white/60 hover:text-white text-sm flex items-center gap-2 transition-colors"
              >
                <Eye className="w-4 h-4" />
                View Blog
              </a>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-white/60 hover:text-white hover:bg-white/10"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl text-text-dark" data-testid="admin-blog-title">
              Blog Admin
            </h1>
            <p className="text-text-dark/60 mt-1">Manage your blog posts</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={handleOpenCreate}
                className="h-11 px-6 rounded-full bg-gradient-to-r from-brand-peach to-brand-lavender text-deep-violet font-semibold"
                data-testid="create-post-btn"
              >
                <Plus className="w-5 h-5 mr-2" />
                New Post
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">
                  {editingPost ? "Edit Post" : "Create New Post"}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6 py-4">
                <div className="space-y-2">
                  <Label>Title *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => handleTitleChange(e.target.value)}
                    placeholder="Post title"
                    className="h-12"
                    data-testid="post-title-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Slug</Label>
                  <Input
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    placeholder="post-url-slug"
                    className="h-12"
                    data-testid="post-slug-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Category</Label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full h-12 rounded-md border border-gray-200 px-3"
                    data-testid="post-category-select"
                  >
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label>Excerpt *</Label>
                  <Textarea
                    value={formData.excerpt}
                    onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                    placeholder="Brief description of the post"
                    className="min-h-[80px]"
                    data-testid="post-excerpt-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Content * (Markdown supported)</Label>
                  <Textarea
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    placeholder="Full post content..."
                    className="min-h-[200px] font-mono text-sm"
                    data-testid="post-content-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Image URL</Label>
                  <Input
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    placeholder="https://..."
                    className="h-12"
                    data-testid="post-image-input"
                  />
                </div>

                <div className="flex gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_featured}
                      onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-brand-primary"
                      data-testid="post-featured-checkbox"
                    />
                    <span className="text-sm text-text-dark">Featured</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_published}
                      onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-brand-primary"
                      data-testid="post-published-checkbox"
                    />
                    <span className="text-sm text-text-dark">Published</span>
                  </label>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => setIsDialogOpen(false)}
                    className="h-11 px-6 rounded-full"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="h-11 px-6 rounded-full bg-gradient-to-r from-brand-peach to-brand-lavender text-deep-violet font-semibold"
                    data-testid="save-post-btn"
                  >
                    {isSaving ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Save className="w-5 h-5 mr-2" />
                        Save
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Posts Table */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-2xl border border-gray-100">
            <p className="text-text-dark/60 text-lg mb-4">No blog posts yet</p>
            <Button
              onClick={handleOpenCreate}
              className="h-11 px-6 rounded-full bg-gradient-to-r from-brand-peach to-brand-lavender text-deep-violet font-semibold"
            >
              <Plus className="w-5 h-5 mr-2" />
              Create Your First Post
            </Button>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="posts-table">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="text-left py-4 px-6 font-medium text-text-dark/70">Title</th>
                    <th className="text-left py-4 px-6 font-medium text-text-dark/70">Category</th>
                    <th className="text-left py-4 px-6 font-medium text-text-dark/70">Date</th>
                    <th className="text-center py-4 px-6 font-medium text-text-dark/70">Status</th>
                    <th className="text-right py-4 px-6 font-medium text-text-dark/70">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {posts.map((post) => (
                    <motion.tr
                      key={post.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="border-b border-gray-50 hover:bg-gray-50/50"
                    >
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          {post.is_featured && (
                            <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                          )}
                          <span className="font-medium text-text-dark">{post.title}</span>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <span className="px-3 py-1 rounded-full bg-gray-100 text-text-dark/70 text-sm">
                          {post.category}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-text-dark/60">
                        {formatDate(post.created_at)}
                      </td>
                      <td className="py-4 px-6 text-center">
                        <button
                          onClick={() => togglePublished(post)}
                          className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm ${
                            post.is_published
                              ? "bg-green-100 text-green-700"
                              : "bg-gray-100 text-gray-600"
                          }`}
                          data-testid={`toggle-publish-${post.id}`}
                        >
                          {post.is_published ? (
                            <>
                              <Eye className="w-3 h-3" />
                              Published
                            </>
                          ) : (
                            <>
                              <EyeOff className="w-3 h-3" />
                              Draft
                            </>
                          )}
                        </button>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => toggleFeatured(post)}
                            className={`p-2 rounded-lg transition-colors ${
                              post.is_featured
                                ? "text-amber-500 bg-amber-50"
                                : "text-gray-400 hover:text-amber-500 hover:bg-amber-50"
                            }`}
                            title={post.is_featured ? "Remove from featured" : "Set as featured"}
                            data-testid={`toggle-featured-${post.id}`}
                          >
                            <Star className={`w-4 h-4 ${post.is_featured ? "fill-amber-500" : ""}`} />
                          </button>
                          <button
                            onClick={() => handleOpenEdit(post)}
                            className="p-2 rounded-lg text-gray-400 hover:text-brand-primary hover:bg-brand-primary/10 transition-colors"
                            data-testid={`edit-post-${post.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(post.id)}
                            className="p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                            data-testid={`delete-post-${post.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
